import { ClaimMembershipData } from './claim-membership-data.model';
import { ClaimMembership } from './claim-membership.model';
import { CustomClaimsData } from './custom-claims-data.model';
import { GlobalRoles } from './global-roles.model';
import { MembershipRoles } from './membership-roles.model';
import { MembershipType } from './membership-type.model';

/** Cached numeric values of GlobalRoles for iteration without per-call allocation */
const GLOBAL_ROLE_VALUES = Object.values(GlobalRoles).filter(
  (v): v is GlobalRoles => typeof v === 'number'
);

/**
 * Custom claims containing global claims and memberships
 */
export class CustomClaims {
  globalClaims: number;
  memberships: ClaimMembership[];

  constructor(globalClaims: number = 0, memberships: ClaimMembership[] = []) {
    this.globalClaims = globalClaims;
    this.memberships = memberships;
  }

  // -- Global role checks --

  hasGlobalRole(role: GlobalRoles): boolean {
    return (this.globalClaims & role) === role;
  }

  hasAnyGlobalRole(roles: GlobalRoles[]): boolean {
    return roles.some(role => this.hasGlobalRole(role));
  }

  hasAllGlobalRoles(roles: GlobalRoles[]): boolean {
    return roles.every(role => this.hasGlobalRole(role));
  }

  getGlobalRoles(): GlobalRoles[] {
    const roles: GlobalRoles[] = [];
    for (const role of GLOBAL_ROLE_VALUES) {
      if (this.hasGlobalRole(role)) roles.push(role);
    }
    return roles;
  }

  isAdmin(): boolean {
    return this.hasGlobalRole(GlobalRoles.Admin) || this.hasGlobalRole(GlobalRoles.SuperAdmin);
  }

  isSuperAdmin(): boolean {
    return this.hasGlobalRole(GlobalRoles.SuperAdmin);
  }

  setGlobalRole(role: GlobalRoles): void {
    this.globalClaims |= role;
  }

  removeGlobalRole(role: GlobalRoles): void {
    this.globalClaims &= ~role;
  }

  // -- Membership checks --

  getMembership(organizationId: string): ClaimMembership | undefined {
    return this.memberships.find(m => m.organizationId === organizationId);
  }

  hasRoleInOrganization(organizationId: string, role: MembershipRoles): boolean {
    return this.getMembership(organizationId)?.hasRole(role) ?? false;
  }

  hasAnyRoleInOrganization(organizationId: string, roles: MembershipRoles[]): boolean {
    return this.getMembership(organizationId)?.hasAnyRole(roles) ?? false;
  }

  hasAllRolesInOrganization(organizationId: string, roles: MembershipRoles[]): boolean {
    return this.getMembership(organizationId)?.hasAllRoles(roles) ?? false;
  }

  setMembership(organizationId: string, claims: number, membershipType: MembershipType = MembershipType.Provider): void {
    const existing = this.getMembership(organizationId);
    if (existing) {
      existing.claims = claims;
      existing.membershipType = membershipType;
    } else {
      this.memberships.push(new ClaimMembership(organizationId, claims, membershipType));
    }
  }

  removeMembership(organizationId: string): void {
    this.memberships = this.memberships.filter(m => m.organizationId !== organizationId);
  }

  /**
   * Get memberships filtered by type
   */
  getMembershipsByType(type: MembershipType): ClaimMembership[] {
    return this.memberships.filter(m => m.membershipType === type);
  }

  // -- Serialization --

  toJSON(): CustomClaimsData {
    const data: CustomClaimsData = {};
    if (this.globalClaims !== 0) {
      data.c = this.globalClaims;
    }
    if (this.memberships.length > 0) {
      data.m = this.memberships.map(m => m.toJSON());
    }
    return data;
  }

  static fromJSON(json: CustomClaimsData | undefined | null): CustomClaims {
    if (!json) {
      return new CustomClaims();
    }

    const globalClaims = json.c ?? 0;
    const memberships = (json.m ?? []).map((m: ClaimMembershipData) => ClaimMembership.fromJSON(m));

    return new CustomClaims(globalClaims, memberships);
  }

  toCompressedString(): string {
    return JSON.stringify(this.toJSON());
  }

  getSize(): number {
    return this.toCompressedString().length;
  }

  validate(): { valid: boolean; length: number; message?: string } {
    const length = this.getSize();

    if (length > 1000) {
      return {
        valid: false,
        length,
        message: `Custom claims exceed 1000-character limit (${length} characters). Reduce number of memberships.`
      };
    }

    return { valid: true, length };
  }
}
